import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Team } from '../types';

interface TeamContextType {
  leagueId: string | null;
  setLeagueId: (id: string) => void;
  teams: Team[];
  selectedTeamId: string;
  setSelectedTeamId: (id: string) => void;
  setTeams: (teams: Team[]) => void;
  loading: boolean;
  error: string | null;
}

export const TeamContext = createContext<TeamContextType | undefined>(undefined);

interface TeamProviderProps {
  children: React.ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_API_URL;

export const TeamProvider: React.FC<TeamProviderProps> = ({ children }) => {
   const [leagueId, setLeagueId] = useState<string | null>(() => {
    return localStorage.getItem('leagueId');
  });
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(''); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (leagueId) localStorage.setItem('leagueId', leagueId);
  }, [leagueId]);
  

  useEffect(() => {
    const fetchTeams = async () => {
      if (!leagueId) return;
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/api/league/${leagueId}/standings`);
        const standings = response.data.standings.results;

        const teamsData: Team[] = standings.map((team: any) => ({
          teamId: String(team.entry),
          playerName: team.player_name,
        }));

        setTeams(teamsData);
        setSelectedTeamId(String(teamsData[0]?.teamId) ?? '');
      } catch (err) {
        console.error('Error fetching league teams', err);
        setTeams([]);
        setError('Failed to load teams for this league.');
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [leagueId]);

  return (
    <TeamContext.Provider value={{
        leagueId,
        setLeagueId,
        teams,
        setTeams,
        selectedTeamId,
        setSelectedTeamId,
        loading,
        error,
      }}>
      {children}
    </TeamContext.Provider>
  );
};

export function useTeamContext() {
  const ctx = useContext(TeamContext);
  if (!ctx) throw new Error('useTeamContext must be used within a TeamProvider');
  return ctx;
}
